// ---------------------------------------------------------------------------
// A word's meaning, and the control that asks for it.
//
// Two pieces, and the split is the interaction model rather than tidiness:
// `DefinitionWord` is how a word is ASKED about and `DefinitionPopover` is where
// the answer LANDS, and the second is deliberately not a child of the first.
//
// **Three things here were argued the other way when this shipped and were
// overturned on 2026-08-04, after Isaac watched the reveal.** Each reversal is
// written out below beside the part of the old argument that still stands,
// because every one of them looks like a mistake if you only read the reasoning
// it replaced.
//
// ## Not hover-only, and `:hover` is not one of the three ways in
//
// This is the treatment `EndGameScreen`'s score strip was given in `38e48b0`
// and it is copied rather than reinvented, because a screen with two ways of
// asking the same question is a screen nobody learns. A phone has no hover at
// all and tap targets are the primary input on every platform (CLAUDE.md,
// Conventions), so every askable word is a real `<button>` and three separate
// gestures reach it:
//
//   - a TAP or a click,
//   - keyboard FOCUS, as it is tabbed to,
//   - a mouse POINTER ENTER — guarded on `pointerType === 'mouse'`, because a
//     touch fires `pointerenter` on its way to the click and an unguarded
//     handler is a second, invisible tap path firing on the way to the first.
//
// **Nothing dismisses it**, for the reason written out on `ScoreCell`: pointer
// leave would snatch the answer away from a mouse on its way to reading it, and
// a toggle on a second tap would close on the very tap it opened by, since focus
// lands before click on every platform. Picking another word replaces it.
//
// **That rule survived the move to a popup, and it took one thing with it.** The
// old objection to a bubble was partly that a bubble has to be dismissed — a
// panel sitting over the control bar takes the bar away until somebody closes
// it. The answer is `pointer-events-none`: the popup is text and nothing else,
// so a press goes straight through it to whatever it is covering. Nothing under
// it is unreachable, so there is nothing to dismiss, and the screen keeps one
// interaction model instead of gaining a second.
//
// ## OVERTURNED: the answer is a popup, not a line in the layout
//
// It used to be a line in the flow — a slot between the stage and the control
// bar on the reveal, a line under the table on the record page. The argument was
// that a word sits in a table cell beside a score, so a bubble anchored to one
// is either wider than the thing it points at or too small to hold a sentence,
// and that a fixed place under the round the word belongs to cannot mislead.
//
// **What was wrong with it is what a line in the flow does to everything around
// it.** A gloss appearing takes its height from the stage on the reveal and
// pushes a table's neighbours down on the record page — so hovering a word
// reflows the screen, and hovering along a row of them reflows it once per word.
// Isaac reported exactly that. The layout may not move because a pointer crossed
// a word.
//
// So the answer is now `position: fixed`, portalled to `document.body`, and
// placed against the word's own rect. Out of flow: nothing reflows when it
// appears and nothing reflows when it goes.
//
// **The part of the old argument that still stands is that the answer must name
// its word**, and it still does — a panel near a word is not attached to it, a
// press that landed one row off has to be obvious, and `word` is drawn in the
// popup for that reason rather than left to the caller.
//
// **Portalled, because the reveal clips on purpose.** That screen is exactly the
// viewport with `overflow-hidden` (STATUS item 11), so a popup rendered inside
// it would be cut off by the very rule that keeps the reveal on one screen.
// `document.body` is outside every ancestor's overflow, and a fixed element adds
// nothing to the document's scrollable area, so the invariant holds with one
// open: `RevealScreen.test.tsx` asserts both halves of that.
//
// **Clamped horizontally, flipped vertically**, and the two differ because the
// axes do. The panel is up to 320px wide against a 390px phone, so there is no
// room to flip left of a word — it is aligned to the word's left edge and slid
// back inside the viewport when it would overhang, which moves it by pixels and
// keeps it beside its word. Vertically it prefers below, flips above when it
// does not fit, and only clamps when it fits neither, because a panel that
// covers the word it is defining is worse than one on the other side of it.
//
// ## OVERTURNED, but only half: silence is no longer the answer to nothing
//
// The old rule was two rules run together, and only one of them was right.
//
// **Still true, and not to be touched: do not invent a definition.** 94.5% of
// the common list has a gloss and 35.7% of the full validation list does (DESIGN,
// Definitions), so the obscure end of a board has nothing to say, and a game that
// has just told somebody their word was not in the dictionary must not follow it
// with an invented sentence. Nothing here generates anything.
//
// **No longer true: say nothing when there is none.** A press that produces
// nothing reads as broken — the same press produced a sentence on the last word,
// so the difference reads as a fault rather than as an absence. Two words in
// three are the ordinary case, and the ordinary case may not look like a failure.
// So there is no entry is now SAID.
//
// **A refusal and an absence stay indistinguishable**, which is the validity
// oracle rule from DESIGN §7 and is the reason this is one message rather than
// two. `lookupDefinition` collapses a 403, a 404, a dropped connection and a 200
// with no gloss into the same null before this file ever sees it, so there is no
// branch here that could tell them apart even by accident. Both tests assert the
// two render byte-identically.
//
// The press is still acknowledged on the button — the picked word wears a raised
// fill — because that acknowledgement is instant and local and a network answer
// is neither.
//
// ## OVERTURNED on the reveal only: the affordance
//
// `marked` draws the inset shadow that says a word can be asked about. It is on
// by default and the record page keeps it: that page is a dense table where the
// words are the content, and a word that reads as ordinary text there is a
// question nobody knows they can ask.
//
// The reveal turns it off — `AskableWord` passes `marked={false}` — because Isaac
// asked for it off there and the reveal is not a table: it is a handful of large
// mono words on a stage that the room is watching, and a rule under every one of
// them was reported as clutter. **What that costs on a phone is written out on
// `AskableWord`.**
//
// **Whatever `marked` draws, it must never be `text-decoration`.** `underline`
// and `line-through` are the same CSS property, so an `underline` here is not
// drawn beside a caller's `line-through` — it REPLACES it, and which one wins is
// decided by stylesheet order rather than by class order. The first cut of this
// did exactly that and silently took the strikethrough off every ruled-out word
// on the record page, which is the one mark that says the word did not stand.
// Turning the mark OFF cannot reintroduce that — nothing is set at all — and the
// mark that is left is an inset shadow, which cannot collide with a text
// decoration and cannot change the metrics of the cell it is in either.
//
// ## The lemma
//
// A gloss reached through an inflection defines a different word: AAHED carries
// AAH's meaning and ACIDLY carries ACID's. So when `lemma` differs from the
// word asked about, the popup says so — `AAHED — from AAH: to exclaim in
// amazement` — because printing one word's meaning under another word's name is
// a claim the dictionary never made.
// ---------------------------------------------------------------------------

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { definesAnotherWord } from '../../lib/definitions.js';
import type { WordDefinition } from '../../lib/definitions.js';
import type { DefinitionAnswer } from '../../hooks/useWordDefinition.js';

/**
 * A word that can be asked about.
 *
 * The children are what is drawn, so a caller keeps whatever face the word
 * already had — mono and struck through in a record's answer cell, blue in the
 * best row — and gains only the ways in. The button is `inline` and carries no
 * padding of its own for the same reason: it wraps text that is already laid
 * out, and a control that changed the metrics of a table cell would move every
 * row around it.
 *
 * `onPick` is handed the button itself, because the answer is now placed
 * against the word's own rect rather than dropped into a slot somewhere below
 * it. `event.currentTarget` and never `event.target`: the children are a caller's
 * markup and a press can land on a span inside them.
 *
 * `aria-label` names the act rather than repeating the word, which the button
 * already contains: a screen reader reads `COUNTDOWN, what COUNTDOWN means`.
 */
export function DefinitionWord({
  word,
  picked,
  onPick,
  marked = true,
  className,
  children,
}: {
  word: string;
  picked: boolean;
  onPick: (anchor: HTMLElement) => void;
  /** The inset shadow that says this is a question. See the header. */
  marked?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-definition-word={word}
      aria-label={`What ${word} means`}
      onClick={(event) => onPick(event.currentTarget)}
      onFocus={(event) => onPick(event.currentTarget)}
      // Mouse only. A touch fires `pointerenter` on its way to the click, so an
      // unguarded handler here is a second tap path that no phone can see.
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') onPick(event.currentTarget);
      }}
      // The mark is an inset shadow and must never be a text decoration — see
      // the header, and the regression that note is written about. `marked`
      // omits it entirely rather than swapping it for something else: the two
      // states are the same glyphs in the same box, so a word does not move
      // between the surfaces that mark it and the one that does not.
      className={`rounded-chip text-left ${
        marked ? 'shadow-[inset_0_-1px_0_0_var(--c-line-strong)]' : ''
      } ${picked ? 'bg-raised' : ''} ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

// --- Where the answer lands -------------------------------------------------
//
// Two shapes, and the second one is not a leftover. `DefinitionPopover` is for a
// WORD that was pressed, which is the reveal and the record page. `DefinitionLine`
// is for `EndGameScreen`, where the thing pressed is a SCORE CELL: the answer
// there is the second line of a two-line detail block in a fixed place under the
// strip, the line above it has already named the word, and there is no word on
// the screen to float beside — the cell holds a number. Isaac's complaint was
// about hovering a word, and the strip does not have that problem: the block is
// in the flow either way, so nothing there reflows on a press that finds a gloss
// and nothing reflows on one that does not.
//
// What the two DO share is the rule about nothing, which is why they are in one
// file: a word that has no entry says so on every surface, and says it with the
// same words.

/** How far the panel keeps off the edge of the viewport, and off its word. */
const EDGE_MARGIN = 8;
const WORD_GAP = 6;

interface Placement {
  /**
   * The button this was measured against. Held so a position computed for one
   * word is never used to draw another: a second pick would otherwise paint for
   * one frame exactly where the first one was.
   */
  anchor: HTMLElement;
  word: string;
  left: number;
  top: number;
  /** Whether the WORD is still on screen. See `place`. */
  onScreen: boolean;
}

/**
 * Where the panel goes, in viewport coordinates.
 *
 * Pure, and takes both rects and the viewport rather than reading any of them,
 * so the rule is one readable expression rather than something you have to run
 * a browser to know.
 *
 * Horizontal is a CLAMP: aligned to the word's left edge, then slid back inside
 * the viewport if it would overhang. A phone is 390px and the panel is up to
 * 320, so flipping to the other side of the word buys nothing it does not
 * immediately lose again — there is no side with room.
 *
 * Vertical is a FLIP: below the word by preference, above it when below would
 * run off the bottom. Clamping instead would put the panel over the word it is
 * defining, and the word is the thing you are looking at. When it fits neither
 * side — a long gloss on a short viewport — it clamps to the top margin, which
 * is the only remaining answer that keeps all of it on screen.
 *
 * `onScreen` is the other half of clamping, and without it the clamp is a bug.
 * Nothing dismisses an answer, so on a long page — the record — a word can be
 * scrolled away while its answer is still open, and a clamped panel would then
 * park itself against the edge of the viewport pointing at a word two screens
 * up. A panel whose word cannot be seen is hidden until it can be.
 */
function place(
  anchor: DOMRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
): { left: number; top: number; onScreen: boolean } {
  const widest = viewport.width - EDGE_MARGIN - panel.width;
  const left = Math.max(EDGE_MARGIN, Math.min(anchor.left, widest));

  const below = anchor.bottom + WORD_GAP;
  const above = anchor.top - WORD_GAP - panel.height;
  const top =
    below + panel.height <= viewport.height - EDGE_MARGIN
      ? below
      : above >= EDGE_MARGIN
        ? above
        : Math.max(EDGE_MARGIN, viewport.height - EDGE_MARGIN - panel.height);

  const onScreen =
    anchor.bottom > 0 &&
    anchor.top < viewport.height &&
    anchor.right > 0 &&
    anchor.left < viewport.width;

  return { left, top, onScreen };
}

/**
 * What the picked word means, floating beside it.
 *
 * **Out of the flow, and that is the whole point of it.** See the header: this
 * replaced a line in the layout, which resized its surroundings every time a
 * pointer crossed a word. Portalled to `document.body` so no ancestor's overflow
 * can clip it — the reveal clips deliberately — and `position: fixed` so it adds
 * nothing to any scroll height.
 *
 * **Nothing is drawn until there is something to say.** `asking` renders null,
 * so the panel never appears empty and then fills, and there is still no spinner
 * anywhere near this. The button's raised fill is what acknowledges the press.
 *
 * It re-measures on every render and on scroll and resize, because the word
 * moves: the reveal's leaderboard inserts rows above the one being read, and the
 * record page is a long scrolling document. Measured in a layout effect and
 * hidden until it has been, so the first paint is never at the last word's
 * position. The write is guarded on the value, so a measurement that agrees with
 * the current one does not re-render.
 */
export function DefinitionPopover({
  word,
  anchor,
  answer,
}: {
  word: string;
  /** The button the word became, or null when nothing is picked. */
  anchor: HTMLElement | null;
  answer: DefinitionAnswer | null;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const [placed, setPlaced] = useState<Placement | null>(null);
  // Reached through a ref by the scroll listener for the same reason
  // `PayoffBeats` does it: an inline callback is a new function every render,
  // and a listener keyed on it would be torn down and rebound on every one.
  const remeasure = useRef<() => void>(() => {});

  const live = anchor !== null && anchor.isConnected && answer !== null && answer.state !== 'asking';

  useLayoutEffect(() => {
    const measure = () => {
      const box = panel.current;
      if (box === null || anchor === null) return;
      const next = place(anchor.getBoundingClientRect(), box.getBoundingClientRect(), {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setPlaced((previous) =>
        previous !== null &&
        previous.anchor === anchor &&
        previous.word === word &&
        previous.left === next.left &&
        previous.top === next.top &&
        previous.onScreen === next.onScreen
          ? previous
          : { anchor, word, ...next },
      );
    };
    remeasure.current = measure;
    if (live) measure();
  });

  useEffect(() => {
    // **Coalesced to a frame, because measuring is a forced reflow.** A scroll
    // fires far more often than the screen repaints, and `getBoundingClientRect`
    // inside the handler makes the browser lay the page out to answer it — which
    // is layout thrash on the one page long enough to scroll. One measurement
    // per frame is all a repaint can use.
    let frame = 0;
    const onMove = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        remeasure.current();
      });
    };
    // Capture, so a scroll inside any container — the record page's own, a
    // frame's on the reveal — is heard rather than only the window's.
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, []);

  if (!live || answer === null) return null;

  // Placed for THIS word against THIS button, or not yet placed at all. The
  // identity comparison is what stops a second pick painting for one frame
  // where the first one was.
  //
  // **Hidden rather than unmounted** when it is not showable, which covers both
  // the frame before it has been measured and a word that has been scrolled
  // away. Unmounting would take the panel out of the accessibility tree and put
  // it back, and `role="status"` announces on insertion — so a scroll would read
  // the definition out again every time the word crossed the edge of the screen.
  const settled =
    placed !== null && placed.anchor === anchor && placed.word === word && placed.onScreen;

  return createPortal(
    <div
      ref={panel}
      data-definition-popup=""
      // The answer arrives away from the control that asked for it and often
      // without a pointer — a keyboard focus is one of the three ways in — so it
      // is announced rather than left to be found.
      role="status"
      // Geometry inline, skin in classes. The position is computed above and the
      // width is the other half of the same computation: `place` is told the
      // panel's measured width, and a max-width in a utility would be a second
      // copy of a number this file is responsible for.
      style={{
        position: 'fixed',
        left: settled ? placed.left : 0,
        top: settled ? placed.top : 0,
        maxWidth: 'min(320px, calc(100vw - 16px))',
        visibility: settled ? 'visible' : 'hidden',
      }}
      // **`pointer-events-none` is what makes "nothing dismisses it" survive.**
      // The panel is text, so a press goes through it to the control bar, the
      // chevrons or the table row underneath — nothing is made unreachable by an
      // answer sitting over it, and so nothing has to be closed. `z-30` is under
      // every sheet and modal in the app (the reasoning modal is 35) and over the
      // reveal's own pinned bar (10).
      className="pointer-events-none z-30 rounded-card border border-line bg-surface px-3 py-2 shadow-lg"
    >
      <AnswerLine word={word} answer={answer} />
    </div>,
    document.body,
  );
}

/**
 * What the picked word means, as a line in the flow.
 *
 * **One surface draws it this way and it is `EndGameScreen`** — see the note
 * above `place`. Everything else floats. This is not the shape that was
 * overturned: the strip's answer is the second line of a block that is on the
 * screen either way, pressed from a score cell rather than from a word.
 *
 * `showWord` is off there, because the line above has already named the word and
 * the two read as one sentence rather than as two.
 */
export function DefinitionLine({
  word,
  answer,
  showWord = true,
}: {
  word: string;
  answer: DefinitionAnswer | null;
  showWord?: boolean;
}) {
  if (answer === null || answer.state === 'asking') return null;
  return <AnswerLine word={word} answer={answer} showWord={showWord} />;
}

/**
 * The answer itself, in one place so the two shapes cannot come to word it
 * differently. `asking` never reaches here: both callers draw nothing at all
 * while a request is out, which is what keeps a spinner off every surface.
 */
function AnswerLine({
  word,
  answer,
  showWord = true,
}: {
  word: string;
  answer: { state: 'none' } | { state: 'gloss'; definition: WordDefinition };
  showWord?: boolean;
}) {
  return (
    <p
      className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px] text-ink-muted"
      data-testid="word-definition"
      data-definition-for={word}
    >
      {showWord ? (
        <span className="shrink-0 font-mono font-bold tracking-[0.06em] text-ink">
          {word.toUpperCase()}
        </span>
      ) : null}
      {answer.state === 'none' ? (
        // **The one message, for every way there can be nothing.** A refusal and
        // a word no dictionary defines must read the same — see the header — and
        // this is the only place either of them is drawn, on any surface.
        //
        // It says the ENTRY is missing rather than anything about the word,
        // because this game has just ruled on some of these words and the two
        // verdicts must not be confused: `not a word` is the round's, and this is
        // not a second one. That also rules out the shorter `not in the
        // dictionary`, which is the ruling's own wording. Isaac's phrasing was
        // `no definition found`; `found` describes a lookup that failed, which is
        // this app's business rather than the reader's.
        <span className="min-w-0 text-ink-dim">no definition for this one</span>
      ) : (
        <>
          {definesAnotherWord(word, answer.definition) ? (
            <span className="label-mono shrink-0">
              from {answer.definition.lemma.toUpperCase()}
            </span>
          ) : null}
          {answer.definition.partOfSpeech === null ? null : (
            <span className="label-mono shrink-0">{answer.definition.partOfSpeech}</span>
          )}
          <span className="min-w-0">{answer.definition.gloss}</span>
        </>
      )}
    </p>
  );
}
